/**
 * Permission resolution.
 *
 * A user's permissions come from their role, plus per-user grants, minus
 * per-user revocations. The exceptions exist because real businesses have
 * them — one apprentice who is allowed to see pricing, one office manager who
 * is not allowed to issue credit notes — and the alternative is a role per
 * person, which stops being a role system.
 *
 * Revocation wins over both the role and an explicit grant. If the two are
 * ever in conflict the safe reading is the restrictive one, and "why can this
 * person still see X" is a worse conversation than "why can't they".
 */

export const PERMISSIONS = [
  'JOB_VIEW',
  'JOB_VIEW_ALL',
  'JOB_EDIT',
  'JOB_DELETE',
  'CLIENT_VIEW',
  'CLIENT_EDIT',
  'CLIENT_DELETE',
  'SCHEDULE_VIEW',
  'SCHEDULE_VIEW_ALL',
  'SCHEDULE_EDIT',
  'QUOTE_VIEW',
  'QUOTE_EDIT',
  'QUOTE_SEND',
  'INVOICE_VIEW',
  'INVOICE_EDIT',
  'INVOICE_ISSUE',
  // Separate from ISSUE, and paired with QUOTE_SEND. Issuing commits the
  // business to a number; sending tells a customer. They are different acts —
  // one is accounting and one is correspondence — and an office junior who may
  // email an invoice is not necessarily one who may raise it.
  'INVOICE_SEND',
  'PAYMENT_VIEW',
  'PAYMENT_RECORD',
  'PAYMENT_REFUND',
  'CATALOG_VIEW',
  'CATALOG_EDIT',
  'FORM_VIEW',
  'FORM_EDIT',
  'INBOX_VIEW',
  'INBOX_MANAGE',
  // The office task board. Not `JOB_EDIT`, which every technician holds and
  // which already covers a job's own follow-ups: office admin — chase the
  // Bunnings account, book the ute service — is not field staff's to read.
  'TASK_MANAGE',
  // The business's reusable proposals. Not `QUOTE_EDIT`: a template is what
  // every future proposal starts from, so an edit reaches customers the editor
  // never sees. Decision 80.
  'PROPOSAL_TEMPLATE_MANAGE',
  // The job card's Assistant tab. Only the door: every tool it runs is an
  // ordinary procedure checking its own permission as the person asking.
  // Decision 94.
  'ASSISTANT_USE',
  // The saved emails and texts in the compose window's Templates list. Not
  // `INBOX_MANAGE`: like a proposal template, an edit reaches every customer
  // somebody later writes to with it. Decision 95.
  'MESSAGE_TEMPLATE_MANAGE',
  'REPORT_VIEW',
  'REPORT_FINANCIAL',
  'USER_MANAGE',
  'ROLE_MANAGE',
  'SETTINGS_MANAGE',
  'AUDIT_VIEW',
  'MIGRATION_RUN',
] as const

export type Permission = (typeof PERMISSIONS)[number]

export interface PermissionSources {
  rolePermissions: readonly Permission[]
  extraPermissions?: readonly Permission[]
  revokedPermissions?: readonly Permission[]
}

export function effectivePermissions(sources: PermissionSources): Set<Permission> {
  const granted = new Set<Permission>(sources.rolePermissions)

  for (const permission of sources.extraPermissions ?? []) granted.add(permission)
  for (const permission of sources.revokedPermissions ?? []) granted.delete(permission)

  return granted
}

export function hasPermission(
  granted: ReadonlySet<Permission>,
  required: Permission,
): boolean {
  return granted.has(required)
}

export function hasAllPermissions(
  granted: ReadonlySet<Permission>,
  required: readonly Permission[],
): boolean {
  return required.every((permission) => granted.has(permission))
}

export function hasAnyPermission(
  granted: ReadonlySet<Permission>,
  required: readonly Permission[],
): boolean {
  return required.some((permission) => granted.has(permission))
}

/**
 * Whether the user may see records belonging to other people.
 *
 * The `_VIEW` / `_VIEW_ALL` pair is what keeps a technician's job list to
 * their own work without a second permission system: `JOB_VIEW` means "the
 * jobs page exists for you", `JOB_VIEW_ALL` means "and it is not filtered to
 * you". Read queries consult this rather than hard-coding a role name.
 */
export function scopeForViewer(granted: ReadonlySet<Permission>): {
  jobs: 'ALL' | 'OWN'
  schedule: 'ALL' | 'OWN'
} {
  return {
    jobs: granted.has('JOB_VIEW_ALL') ? 'ALL' : 'OWN',
    schedule: granted.has('SCHEDULE_VIEW_ALL') ? 'ALL' : 'OWN',
  }
}

/**
 * The built-in roles, created by the seed and not deletable.
 *
 * Deliberately few. Every extra role is a decision someone has to make on the
 * invite screen, and per-user grants cover the exceptions.
 */
export const SYSTEM_ROLES: Record<string, { description: string; permissions: Permission[] }> = {
  Owner: {
    description: 'Full access, including users, roles, settings and financials.',
    permissions: [...PERMISSIONS],
  },
  Office: {
    description: 'Runs the office: scheduling, quoting, invoicing, payments.',
    permissions: [
      'JOB_VIEW',
      'JOB_VIEW_ALL',
      'JOB_EDIT',
      'JOB_DELETE',
      'CLIENT_VIEW',
      'CLIENT_EDIT',
      'SCHEDULE_VIEW',
      'SCHEDULE_VIEW_ALL',
      'SCHEDULE_EDIT',
      'QUOTE_VIEW',
      'QUOTE_EDIT',
      'QUOTE_SEND',
      'INVOICE_VIEW',
      'INVOICE_EDIT',
      'INVOICE_ISSUE',
      'INVOICE_SEND',
      'PAYMENT_VIEW',
      'PAYMENT_RECORD',
      'CATALOG_VIEW',
      'CATALOG_EDIT',
      'FORM_VIEW',
      'FORM_EDIT',
      'INBOX_VIEW',
      'INBOX_MANAGE',
      'TASK_MANAGE',
      'PROPOSAL_TEMPLATE_MANAGE',
      'ASSISTANT_USE',
      'MESSAGE_TEMPLATE_MANAGE',
      'REPORT_VIEW',
    ],
  },
  Technician: {
    description: 'Field staff: their own jobs and schedule, time, forms and billing on site.',
    permissions: [
      'JOB_VIEW',
      'JOB_EDIT',
      'CLIENT_VIEW',
      'SCHEDULE_VIEW',
      'CATALOG_VIEW',
      'FORM_VIEW',
      // Filling in a report is the technician's job, so `FORM_EDIT` is theirs.
      // It governs answers only. Changing what a form *asks* is a different
      // act with a different blast radius — one edit reshapes every future
      // report — and stays behind `SETTINGS_MANAGE`, which no technician has.
      'FORM_EDIT',
      // Billing on site: items on the job's invoice, issuing it and writing
      // down money taken at the door. Not sending, refunding or the ledger's
      // reports, and the invoice and quote lists stay to their own jobs.
      'QUOTE_VIEW',
      'INVOICE_VIEW',
      'INVOICE_EDIT',
      'INVOICE_ISSUE',
      'PAYMENT_RECORD',
    ],
  },
  'Read only': {
    description: 'Sees everything, changes nothing. For accountants and auditors.',
    permissions: [
      'JOB_VIEW',
      'JOB_VIEW_ALL',
      'CLIENT_VIEW',
      'SCHEDULE_VIEW',
      'SCHEDULE_VIEW_ALL',
      'QUOTE_VIEW',
      'INVOICE_VIEW',
      'PAYMENT_VIEW',
      'CATALOG_VIEW',
      'FORM_VIEW',
      'REPORT_VIEW',
      'REPORT_FINANCIAL',
      'AUDIT_VIEW',
    ],
  },
}
